import { Model } from 'app/model/backendmodel';
import { Http, Response } from 'app/services/http';
import { AuthService } from '../authentication/auth.service';
import { LoggerService } from 'app/logger/logger.service';
import * as Constants from '../globals';
import { BaseMetadataClient, IMetadataClient } from '../base-metadata-client';
import { WorkspaceUrlInformationService } from '../workspace-url-information.service';
import { notImplementedFunction } from 'app/extension/api/common';
import { IDataSourceCredentialUrlResponse, IAzureResource, ISOCDataLakeAnalyticsAccount, IAzureResourceShadow } from 'app/interfaces/IAzureResource';
import { format } from 'app/shared/utils/string.util';
import * as HttpUtil from 'app/shared/utils/http.utils';
import { LoggerHelper } from 'app/logger/logger-helper';
import { retryWhen } from 'rxjs/operators';
import { AuthConstants } from '../authentication/auth-constants';
import { getAzureEnvironment } from 'environments/environment.util';
import { getCurrentEnvironment } from 'environments/environment.current';
import { IResourceContext, ModelConstants } from 'shell-models-lib';
import { A365WorkspaceCacheService } from './A365workspace-cache.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ActionFailedEventRow, ActionSucceedEventRow } from 'app/logger/logger.action-result-event';
import { Inject } from '@angular/core';
import { RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';

export class A365MetadataClient extends BaseMetadataClient {
    public className = 'A365MetadataClient';

    /**
     * Data plane metadata client, so that it can be used in case ARM call fails with a 403.
     * Note, this is a public property rather than passed in the constructor since data plane client
     * also depends on this client.
     */
    public dataplaneMetadataClient: IMetadataClient;
    protected apiVersion: string = Constants.WORKSPACE_APIVERSION;

    public constructor(
        @Inject(RESOURCE_CONTEXT_DATA) _contextData: IResourceContext,
        readonly httpClient: Http,
        readonly authService: AuthService,
        private readonly _workspaceCacheService: A365WorkspaceCacheService,
        protected readonly urlService: WorkspaceUrlInformationService,
        protected readonly logger: LoggerService,
        protected readonly loggerHelper: LoggerHelper,
    ) {
        super(_contextData, httpClient, authService, urlService, logger, loggerHelper);
    }

    public async getResource(resourceType?: Constants.ResourceType, resourceName?: string): Promise<IAzureResource> {
        try {
            if (!resourceType && !resourceName) {
                if (this.urlService.workspaceName) {
                    return await super.getResource();
                }
                return null;
            } else {
                return await super.getResource(resourceType, resourceName);
            }
        } catch (e) {
            if (this._isRetryableFailure(e, resourceType)) {
                this.logger.log(this.className, `getResource - retrying ${resourceName} with type ${resourceType} using data plane.`);
                return this.dataplaneMetadataClient.getResource(resourceType, resourceName);
            }
            throw e;
        }
    }

    public async listResources(resourceType: Constants.ResourceType): Promise<IAzureResource[]> {
        return this._listResources(resourceType);
    }

    public async listResourceShadows(resourceType: Constants.ResourceType): Promise<IAzureResourceShadow[]> {
        try {
            return await super.listResourceShadows(resourceType);
        } catch (e) {
            if (this._isRetryableFailure(e, resourceType)) {
                this.logger.log(this.className, `listResourceShadows - retrying list call with type ${resourceType} using data plane.`);
                return this.dataplaneMetadataClient.listResourceShadows(resourceType);
            }
            throw e;
        }
    }

    public upgradeLinkedService(linkedServiceName: string): Promise<Response> {
        return notImplementedFunction();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async getFirstPage(resourceType: Constants.ResourceType, complexResourceType?: string, apiVersion?: string): Promise<any> {
        try {
            return await super.getFirstPage(resourceType, complexResourceType, apiVersion);
        } catch (e) {
            if (this._isRetryableFailure(e, resourceType)) {
                this.logger.log(this.className, `getFirstPage - retrying call with type ${resourceType} using data plane.`);
                return this.dataplaneMetadataClient.getFirstPage(resourceType);
            }
            if (this._workspaceCacheService.isWorkspaceLite() && resourceType === ModelConstants.sqlPool && e && e.status === 403) {
                this._workspaceCacheService.sqlPoolNoPermission$.next(true);
                return {};
            }
            throw e;
        }
    }

    public getDataSourceCredentialUrl(lsName: string, sessionId: string): Promise<IDataSourceCredentialUrlResponse> {
        return notImplementedFunction();
    }

    public getSparkHistoryEndpoint(baseUri: string): Promise<string> {
        const shsUrl: string = format('{0}/sparkhistory/api/v1/historyServerProperties', baseUri);

        return new Promise<string>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync(AuthConstants.ARTIFACTS_ENDPOINT);
            this.httpClient.get(shsUrl, { headers })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    const resource = resp.json();
                    resolve(resource.webProxyEndpoint);
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(this.className, 'getSparkHistoryEndpoint: Error while calling url', reason, { getSparkHistoryEndpoint: shsUrl });
                    reject(reason);
                });
        });
    }

    public getSparkHistoryServerVersion(workspace: Model.Workspace): Promise<string> {
        const hsPropertiesAPIUrl: string = format('{0}/sparkhistory/api/v1/historyServerProperties', workspace.properties.connectivityEndpoints.dev);
        return new Promise<string>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync(AuthConstants.ARTIFACTS_ENDPOINT);
            this.httpClient.get(hsPropertiesAPIUrl, { headers })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    const jResp = resp.json();
                    this.logger.logEvent(new ActionSucceedEventRow('GetHistoryServerVersion'));
                    resolve(jResp.version);
                })
                .catch((err: HttpErrorResponse) => {
                    this.logger.logEvent(new ActionFailedEventRow('GetHistoryServerVersion', err));
                    this.loggerHelper.logRequestError(
                        this.className,
                        'getSparkHistoryServerVersion: Error while calling url, final history version is 2.4',
                        err,
                        { getSparkHistoryServerVersion: hsPropertiesAPIUrl });
                    resolve('2.4');
                });
        });
    }

    public async getSOCDataLakeAnalyticsAccount(workspace: Model.Workspace): Promise<ISOCDataLakeAnalyticsAccount> {
        if (workspace && workspace.properties && workspace.properties.adlaResourceId) {
            const basicUrl = getAzureEnvironment(getCurrentEnvironment()).resourceManager;
            const url = `${basicUrl}${workspace.properties.adlaResourceId}?api-version=2015-10-01-preview`;
            const headers = await this.getDefaultHeadersAsync(AuthConstants.AUTH_AREA);
            return this.httpClient.get(url, { headers })
                .toPromise()
                .then(async (resp) => {
                    const response = resp.json();
                    return response;
                })
                .catch((error) => {
                    this.loggerHelper.logRequestError(this.className, 'getSOCDataLakeAnalyticsAccount: Error while calling url', error, { getSOCDataLakeAnalyticsAccount: url });
                    return null;
                });
        }
        return null;
    }
    public async rename(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<Response> {
        return notImplementedFunction();
    }

    protected _createResourceBaseUri(resourceType: Constants.ResourceType): string {
        if (this._workspaceCacheService.isWorkspaceLite() && resourceType === ModelConstants.sqlPool) {
            return `${AuthConstants.RESOURCE_MANAGER_ENDPOINT}subscriptions/${this.urlService.subscriptionId}/resourceGroups/${this.urlService.resourceGroupName}/providers/Microsoft.Sql/servers/${this.urlService.resourceName}/databases`;
        }
        return super._createResourceBaseUri(resourceType);
    }

    protected _getApiVersion(resourceType: Constants.ResourceType): string {
        if (this._workspaceCacheService.isWorkspaceLite() && resourceType === ModelConstants.sqlPool) {
            return '2017-10-01-preview';
        }
        if (!resourceType || resourceType === 'workspace') {
            return Constants.WORKSPACE_WORKSPACE_2021_06_01;
        }
        return super._getApiVersion(resourceType);
    }

    private _isRetryableFailure(error: Response, resourceType?: Constants.ResourceType): boolean {
        const retryableTypes: Constants.ResourceType[] = ['sqlPool', 'bigDataPool', 'workspace', 'integrationRuntime'];

        return !this._workspaceCacheService.isWorkspaceLite()
            && error instanceof Response
            && error.status === 403
            && (!resourceType || retryableTypes.map((supportedType) => supportedType.toLowerCase()).includes(resourceType.toLowerCase()));
    }

    private async _listResources(resourceType: Constants.ResourceType, apiVersion?: string): Promise<IAzureResource[]> {
        try {
            return await super.listResources(resourceType, apiVersion);
        } catch (e) {
            if (this._isRetryableFailure(e, resourceType)) {
                this.logger.log(this.className, `listResources - retrying list call with type ${resourceType} using data plane.`);
                return this.dataplaneMetadataClient.listResources(resourceType);
            }
            if (this._workspaceCacheService.isWorkspaceLite() && resourceType === ModelConstants.sqlPool && e && e.status === 403) {
                this._workspaceCacheService.sqlPoolNoPermission$.next(true);
                return [];
            }
            throw e;
        }
    }
}
